import "@/App.css";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { PublicLayout } from "@/components/layout/PublicLayout";
import HomePage from "@/pages/HomePage";
import BranchesPage from "@/pages/BranchesPage";
import PropertyDetailsPage from "@/pages/PropertyDetailsPage";
import AdvancedSearchPage from "@/pages/AdvancedSearchPage";
import ContactPage from "@/pages/ContactPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardOverviewPage from "@/pages/dashboard/DashboardOverviewPage";
import DashboardPropertiesPage from "@/pages/dashboard/DashboardPropertiesPage";
import DashboardAddPropertyPage from "@/pages/dashboard/DashboardAddPropertyPage";
import DashboardLeadsPage from "@/pages/dashboard/DashboardLeadsPage";
import DashboardLeadDetailsPage from "@/pages/dashboard/DashboardLeadDetailsPage";
import DashboardSettingsPage from "@/pages/dashboard/DashboardSettingsPage";
import DashboardAdminPage from "@/pages/dashboard/DashboardAdminPage";

const LocalizedRoute = ({ component: Component }) => {
  const { lang } = useParams();

  if (!["ar", "en"].includes(lang)) {
    return <Navigate to="/ar" replace />;
  }

  return <Component lang={lang} />;
};

const LocalizedPublicLayout = () => {
  const { lang } = useParams();

  if (!["ar", "en"].includes(lang)) {
    return <Navigate to="/ar" replace />;
  }

  return <PublicLayout />;
};

const LocalizedDashboardLayout = () => {
  const { lang } = useParams();

  if (!["ar", "en"].includes(lang)) {
    return <Navigate to="/ar" replace />;
  }

  return (
    <ProtectedRoute lang={lang}>
      <DashboardLayout lang={lang}>
        <Outlet />
      </DashboardLayout>
    </ProtectedRoute>
  );
};

const LocalizedDashboardPage = ({ component: Component }) => {
  const { lang } = useParams();

  if (!["ar", "en"].includes(lang)) {
    return <Navigate to="/ar" replace />;
  }

  return <Component lang={lang} />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/ar" replace />} />

          <Route path="/:lang" element={<LocalizedPublicLayout />}>
            <Route index element={<LocalizedRoute component={HomePage} />} />
            <Route path="branches" element={<LocalizedRoute component={BranchesPage} />} />
            <Route path="properties/:propertyId" element={<LocalizedRoute component={PropertyDetailsPage} />} />
            <Route path="search" element={<LocalizedRoute component={AdvancedSearchPage} />} />
            <Route path="contact" element={<LocalizedRoute component={ContactPage} />} />
          </Route>

          <Route path="/:lang/staff/login" element={<LocalizedRoute component={LoginPage} />} />
          <Route path="/:lang/staff/register" element={<LocalizedRoute component={RegisterPage} />} />

          <Route path="/:lang/dashboard" element={<LocalizedDashboardLayout />}>
            <Route index element={<LocalizedDashboardPage component={DashboardOverviewPage} />} />
            <Route path="properties" element={<LocalizedDashboardPage component={DashboardPropertiesPage} />} />
            <Route path="properties/new" element={<LocalizedDashboardPage component={DashboardAddPropertyPage} />} />
            <Route path="properties/:propertyId/edit" element={<LocalizedDashboardPage component={DashboardAddPropertyPage} />} />
            <Route path="leads" element={<LocalizedDashboardPage component={DashboardLeadsPage} />} />
            <Route path="leads/:leadId" element={<LocalizedDashboardPage component={DashboardLeadDetailsPage} />} />
            <Route path="settings" element={<LocalizedDashboardPage component={DashboardSettingsPage} />} />
            <Route path="admin" element={<LocalizedDashboardPage component={DashboardAdminPage} />} />
          </Route>

          <Route path="*" element={<Navigate to="/ar" replace />} />
        </Routes>

        <Toaster
          position="top-center"
          richColors
          toastOptions={{
            style: {
              borderRadius: "14px",
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
